import { useCallback, useEffect, useState } from 'react';

import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Paper from '@mui/material/Paper';

import { ensureDefined } from '@e-socna/utils';

import { SplashScreen } from '../../../SplashScreen';
import {
  MemoBeforeSubmitForm,
  emptyMemosState,
} from '../../../../forms/MemoBeforeSubmit';

import { NoData } from '../../../Illustrations/NoData';
import { SpeedDial } from '../../../SpeedDial';
import { Dialog } from '../../../Dialog';

import { useDocumentEditorContext } from '../../contexts/DocumentEditorContext/hooks/useDocumentEditorContext.hook';
import type { Editable } from '../../types/editable.type';

import { Table } from './Table';

export const GenerationTabPanel = () => {
  const {
    state,
    enrichedVirtualRoot,
    props: {
      formSettings: { initialEditable, isLoading },
      getters: { printing: getPrinting },
      handlers: { onPrint },
      saveSettings: { askMemo },
    },
    handleSaveAction,
  } = useDocumentEditorContext<Editable>();

  const { handlePrint } = ensureDefined({ handlePrint: onPrint });

  const askMemoBeforePrint = askMemo?.beforePrint ?? true;

  const [isCreatingNewVersion, setIsCreatingNewVersion] = useState(false);
  const [hasCheckedIfSaved, setHasCheckedIfSaved] = useState(false);
  const [hasCheckedIfUpToDate, setHasCheckedIfUpToDate] = useState(false);
  const [isVisibleSaveFirstDialog, setIsVisibleSaveFirstDialog] =
    useState(false);
  const [
    isVisibleForceCreateNewVersionDialog,
    setIsVisibleForceCreateNewVersionDialog,
  ] = useState(false);

  const handleCancelSaveFirst = useCallback(() => {
    setIsVisibleSaveFirstDialog(false);
    setHasCheckedIfSaved(true);
  }, []);

  const handleCancelForceCreate = useCallback(() => {
    setIsVisibleSaveFirstDialog(false);
    setIsVisibleForceCreateNewVersionDialog(false);
    setHasCheckedIfSaved(false);
    setHasCheckedIfUpToDate(false);
    setIsCreatingNewVersion(false);
  }, []);

  const [printMemos, setPrintMemos] = useState(emptyMemosState);
  const clearPrintMemos = useCallback(() => {
    setPrintMemos(emptyMemosState);
  }, []);

  const print = useCallback(() => {
    handlePrint({
      clearTmp: true,
      handleError: () => {
        setIsCreatingNewVersion(false);
        setHasCheckedIfSaved(false);
        setHasCheckedIfUpToDate(false);
        setIsVisibleSaveFirstDialog(false);
        setIsVisibleForceCreateNewVersionDialog(false);
      },
      handleSuccess: () => {
        setIsCreatingNewVersion(false);
        setHasCheckedIfSaved(false);
        setHasCheckedIfUpToDate(false);
        setIsVisibleSaveFirstDialog(false);
        setIsVisibleForceCreateNewVersionDialog(false);
        clearPrintMemos();
      },
      isTmp: false,
      memos: askMemoBeforePrint ? printMemos : emptyMemosState,
      useCacheFirst: false,
    });
  }, [askMemoBeforePrint, handlePrint, printMemos, clearPrintMemos]);

  const cancelMemoSubmit = useCallback(() => {
    setIsCreatingNewVersion(false);
    setHasCheckedIfSaved(false);
    setHasCheckedIfUpToDate(false);
    setIsVisibleSaveFirstDialog(false);
    setIsVisibleForceCreateNewVersionDialog(false);
    clearPrintMemos();
  }, [clearPrintMemos]);

  const handlePrintAction = useCallback(() => {
    askMemoBeforePrint
      ? setPrintMemos((oldMemos) => ({
          ...oldMemos,
          isOpenMemoDialog: true,
        }))
      : print();
  }, [askMemoBeforePrint, print]);

  const handleConfirmForceCreate = useCallback(() => {
    setIsVisibleSaveFirstDialog(false);
    setIsVisibleForceCreateNewVersionDialog(false);
    setHasCheckedIfUpToDate(true);
    handlePrintAction();
  }, [handlePrintAction]);

  const { pdfPrints } = getPrinting(initialEditable);
  const notTmpPrints = pdfPrints.filter((pdfPrint) => !pdfPrint.isTmp);

  useEffect(() => {
    if (isCreatingNewVersion && enrichedVirtualRoot.isFullyLoaded) {
      if (!hasCheckedIfSaved) {
        const notSaved =
          state.pastVersions.length &&
          state.currentVersion !== state.latestSavedVersion;

        if (notSaved) {
          // TODO : check if we can refactor this code in order to respect this rule
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setIsVisibleSaveFirstDialog(true);
          return;
        }

        setIsVisibleSaveFirstDialog(false);
        setHasCheckedIfSaved(true);
        return;
      }

      if (!hasCheckedIfUpToDate) {
        const lastPrint = notTmpPrints.reduce(
          (max, pdfPrint) =>
            new Date(max?.createdAt ?? '01/01/1970') >
            new Date(pdfPrint.createdAt)
              ? max
              : pdfPrint,
          notTmpPrints[0],
        );

        if (
          lastPrint &&
          new Date(lastPrint.createdAt) >=
            new Date(initialEditable.contentUpdatedAt)
        ) {
          setIsVisibleForceCreateNewVersionDialog(true);
          return;
        }

        setIsVisibleForceCreateNewVersionDialog(false);
        setHasCheckedIfUpToDate(true);
        handlePrintAction();
        return;
      }
    }
  }, [
    state.currentVersion,
    state.latestSavedVersion,
    state.pastVersions.length,
    enrichedVirtualRoot.isFullyLoaded,
    handlePrintAction,
    hasCheckedIfSaved,
    hasCheckedIfUpToDate,
    initialEditable.contentUpdatedAt,
    isCreatingNewVersion,
    notTmpPrints,
  ]);

  const letsPrint = useCallback(() => {
    setIsCreatingNewVersion(true);
  }, []);

  if (!enrichedVirtualRoot.subSections.length) {
    return <NoData />;
  }

  const sortedNotTmpPrints = notTmpPrints
    .slice()
    .sort((a, b) => (a.increment <= b.increment ? 1 : -1));

  return (
    <>
      <Dialog
        cancelLabel="Non"
        confirmLabel="Oui"
        content="Souhaitez-vous sauvegarder les modifications avant de générer le PDF ?"
        id="save-first-dialog"
        isLoading={isLoading}
        isVisible={isVisibleSaveFirstDialog}
        onCancel={handleCancelSaveFirst}
        onConfirm={handleSaveAction}
        setIsVisible={setIsVisibleSaveFirstDialog}
        title="Voulez-vous d'abord sauvegarder ?"
      />
      <Dialog
        cancelLabel="Non"
        confirmLabel="Oui"
        content="Souhaitez-vous malgré tout créer une nouvelle version ?"
        id="force-create-new-version-dialog"
        isLoading={isLoading}
        isVisible={isVisibleForceCreateNewVersionDialog}
        onCancel={handleCancelForceCreate}
        onConfirm={handleConfirmForceCreate}
        setIsVisible={setIsVisibleForceCreateNewVersionDialog}
        title="La version actuelle est déjà la dernière version"
      />
      <SpeedDial
        actions={[
          {
            id: 'newVersion',
            icon: <PictureAsPdfIcon />,
            name: 'Nouvelle Version',
            isDisabled: isCreatingNewVersion,
            onClick: letsPrint,
          },
        ]}
        title="Actions possibles sur l'Historique des Impressions"
      />
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!askMemoBeforePrint && (
        <MemoBeforeSubmitForm
          clearMemos={clearPrintMemos}
          isLoading={isLoading}
          memos={printMemos}
          onCancel={cancelMemoSubmit}
          onConfirm={print}
          setMemos={setPrintMemos}
          submitLabel="Générer"
        />
      )}
      {sortedNotTmpPrints.length ? (
        <Paper
          sx={{
            mt: 4,
            display: 'flex',
            height: 'fit-content',
            flexGrow: 1,
            maxWidth: '100%',
          }}
        >
          <Table rows={sortedNotTmpPrints} />
        </Paper>
      ) : (
        <SplashScreen
          action={{
            label: 'Imprimer',
            onClick: letsPrint,
            disabled: isCreatingNewVersion,
          }}
          illustration={<NoData />}
          message="Vous n'avez pas encore généré de PDF pour ce document"
        />
      )}
    </>
  );
};
